/**
 * Worklist priority rule service — facility worklist ordering configuration.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../utils/apiClient';

export type WorklistCriterionType =
  | 'modality'
  | 'order_priority'
  | 'subspecialty'
  | 'tat_status'
  | 'clinical_flag';

interface WorklistPriorityRuleModality {
  id: number;
  name: string;
}

export interface WorklistPriorityRule {
  id: number;
  facilityId: number;
  name: string;
  criterionType: WorklistCriterionType;
  criterionValue: string;
  criterionValueDisplay?: string;
  modalityId: number | null;
  modality: WorklistPriorityRuleModality | null;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface CreateWorklistPriorityRuleRequest {
  facilityId?: number;
  name: string;
  criterionType: WorklistCriterionType;
  criterionValue: string;
  modalityId?: number | null;
  isActive?: boolean;
}

interface UpdateWorklistPriorityRuleRequest extends Partial<CreateWorklistPriorityRuleRequest> {}

interface ApiListResponse {
  success?: boolean;
  message?: string;
  data?: WorklistPriorityRule[];
}

interface ApiSingleResponse {
  success?: boolean;
  message?: string;
  data?: WorklistPriorityRule;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.response && typeof error.response === 'object') {
      const response = error.response as { message?: string; error?: string };
      if (response.message) {
        return response.message;
      }
      if (response.error) {
        return response.error;
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Worklist priority rule request failed';
}

function toCreateApiPayload(payload: CreateWorklistPriorityRuleRequest) {
  return {
    facilityId: payload.facilityId,
    name: payload.name,
    criterionType: payload.criterionType,
    criterionValue: payload.criterionValue,
    modalityId: payload.modalityId ?? null,
    isActive: payload.isActive,
  };
}

function toUpdateApiPayload(payload: UpdateWorklistPriorityRuleRequest) {
  const body: Record<string, unknown> = {};

  if (payload.facilityId !== undefined) {
    body.facilityId = payload.facilityId;
  }
  if (payload.name !== undefined) {
    body.name = payload.name;
  }
  if (payload.criterionType !== undefined) {
    body.criterionType = payload.criterionType;
  }
  if (payload.criterionValue !== undefined) {
    body.criterionValue = payload.criterionValue;
  }
  if (payload.modalityId !== undefined) {
    body.modalityId = payload.modalityId;
  }
  if (payload.isActive !== undefined) {
    body.isActive = payload.isActive;
  }

  return body;
}

export async function listWorklistPriorityRules(facilityId?: number): Promise<{
  success: boolean;
  data?: WorklistPriorityRule[];
  error?: string;
}> {
  try {
    const url = API_ENDPOINTS.WORKLIST_PRIORITY_RULES.LIST(facilityId);
    const response = await apiGet<ApiListResponse>(url);
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to load worklist priority rules' };
    }

    if (Array.isArray(body?.data)) {
      return { success: true, data: body.data };
    }

    return { success: false, error: 'Invalid worklist priority rules response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function createWorklistPriorityRule(
  payload: CreateWorklistPriorityRuleRequest
): Promise<{ success: boolean; data?: WorklistPriorityRule; error?: string }> {
  try {
    const response = await apiPost<ApiSingleResponse>(
      API_ENDPOINTS.WORKLIST_PRIORITY_RULES.CREATE,
      toCreateApiPayload(payload)
    );
    const body = response.data;

    if (body?.success === false || !body?.data) {
      return { success: false, error: body?.message || 'Failed to create worklist priority rule' };
    }

    return { success: true, data: body.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function updateWorklistPriorityRule(
  id: number,
  payload: UpdateWorklistPriorityRuleRequest
): Promise<{ success: boolean; data?: WorklistPriorityRule; error?: string }> {
  try {
    const response = await apiPut<ApiSingleResponse>(
      API_ENDPOINTS.WORKLIST_PRIORITY_RULES.UPDATE(id),
      toUpdateApiPayload(payload)
    );
    const body = response.data;

    if (body?.success === false || !body?.data) {
      return { success: false, error: body?.message || 'Failed to update worklist priority rule' };
    }

    return { success: true, data: body.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function deleteWorklistPriorityRule(
  id: number,
  facilityId?: number
): Promise<{ success: boolean; error?: string }> {
  try {
    await apiDelete(API_ENDPOINTS.WORKLIST_PRIORITY_RULES.DELETE(id, facilityId));
    return { success: true };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function reorderWorklistPriorityRules(
  ruleIds: number[],
  facilityId?: number
): Promise<{ success: boolean; data?: WorklistPriorityRule[]; error?: string }> {
  try {
    const response = await apiPut<ApiListResponse>(API_ENDPOINTS.WORKLIST_PRIORITY_RULES.REORDER, {
      ruleIds,
      facilityId,
    });
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to reorder worklist priority rules' };
    }

    if (Array.isArray(body?.data)) {
      return { success: true, data: body.data };
    }

    return { success: false, error: 'Invalid reorder response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}
